<template>
  <div class="flex p-4 min-h-screen">
    <!-- Left Section -->
    <div class="w-1/2 items-center">
      <img
        v-if="selectedRole === 'Student'"
        src="@/assets/hero-login-1.png"
        alt="Student"
        class="bg-cover"
      >
      <img
        v-else
        src="@/assets/hero-login-2.png"
        alt="Parent"
        class="bg-cover"
      >
    </div>

    <!-- Right Section -->
    <div class="w-1/2 flex flex-col justify-center px-10 max-w-md mx-auto">
      <img
        src="@/assets/logo.png"
        alt="Logo"
        class="w-20 mb-8 mx-auto"
      >

      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Register As
        </label>
        <SwitchButtonGroup
          v-model="selectedRole"
          :options="roleOptions"
        />
      </div>

      <div class="text-left">
        <h2 class="text-2xl font-bold mb-2">
          Create Your Account {{ selectedRole }}
        </h2>
        <p class="text-gray-500 mb-6">
          {{
            selectedRole === "Student"
              ? "Welcome! let’s create your student account!"
              : "Welcome! let’s create your parent account!"
          }}
        </p>
      </div>

      <form
        class="space-y-4"
        @submit.prevent="register"
      >
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Country</label>
          <select
            v-model="selectedCountryId"
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option
              disabled
              value=""
            >
              Select your country
            </option>
            <option
              v-for="country in countries"
              :key="country.id"
              :value="country.id"
            >
              {{ country.name }}
            </option>
          </select>
          <p
            v-if="countryLoading"
            class="text-xs text-gray-500 mt-1"
          >
            Loading countries...
          </p>
        </div>

        <div class="flex gap-4">
          <div class="w-1/2">
            <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              v-model="firstName"
              type="text"
              placeholder="Enter your first name"
              class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
          </div>
          <div class="w-1/2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input
              v-model="lastName"
              type="text"
              placeholder="Enter your last name"
              class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            v-model="email"
            type="email"
            placeholder="Enter your email"
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
        </div>

        <!-- Password & Confirm Password dalam 1 baris -->
        <div class="flex gap-4">
          <!-- Password -->
          <div class="w-1/2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div class="relative">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Password"
                class="w-full px-4 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
              <button
                type="button"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                @click="showPassword = !showPassword"
              >
                <span v-if="showPassword">🙈</span>
                <span v-else>👁️</span>
              </button>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="w-1/2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <div class="relative">
              <input
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirm password"
                class="w-full px-4 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
              <button
                type="button"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <span v-if="showConfirmPassword">🙈</span>
                <span v-else>👁️</span>
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-start text-sm">
          <input
            v-model="agreeTerms"
            type="checkbox"
            class="mr-2 mt-1"
          >
          <label>I agree to the applicable
            <a
              href="#"
              class="text-blue-600 hover:underline"
            >terms and conditions</a>
          </label>
        </div>

        <BaseButton
          variant="primary"
          color="blue"
          class="w-full flex justify-center rounded-md py-4 mt-2 mb-4"
        >
          <span>Create Account</span>
        </BaseButton>

        <GoogleSignIn />
      </form>

      <p class="mt-6 text-sm text-center text-gray-600">
        Have an account?
        <a
          href="/login"
          class="text-blue-600 hover:underline"
        >Sign In</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { registerApi } from "@/api/authApi";
import BaseButton from "@/components/base/BaseButton.vue";
import SwitchButtonGroup from "@/components/base/SwitchButton.vue";
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import GoogleSignIn from "../GoogleSignIn.vue";
import { GetDataCountry } from "@/api/settingApi";

const roleOptions = ["Student", "Parent", "Tutor"];
const selectedRole = ref(roleOptions[0]);
const countries = ref([]);
const selectedCountryId = ref("");
const countryLoading = ref(false);
const firstName = ref("");
const lastName = ref("");
const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const agreeTerms = ref(false);
const router = useRouter();
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const fallbackCountries = [
  { id: 13, name: "Malaysia" },
  { id: 9, name: "Indonesia" },
  { id: 19, name: "Singapore" },
];

const loadCountries = async () => {
  countryLoading.value = true;
  try {
    const res = await GetDataCountry("", 1, 50);
    const list = Array.isArray(res?.data) ? res.data : [];
    const mapped = list
      .map((c) => ({
        id: c.id,
        name: String(c.countryName || c.CountryName || "").trim(),
      }))
      .filter((c) => c.id && c.name)
      .sort((a, b) => a.name.localeCompare(b.name));
    countries.value = mapped.length ? mapped : fallbackCountries;
  } catch (error) {
    console.error("Failed to load countries:", error);
    countries.value = fallbackCountries;
  } finally {
    if (!selectedCountryId.value && countries.value.length) {
      selectedCountryId.value = countries.value[0].id;
    }
    countryLoading.value = false;
  }
};

onMounted(() => {
  loadCountries();
});

const register = async () => {
  if (!agreeTerms.value) {
    alert("You must agree to the terms and conditions");
    return;
  }

  if (password.value !== confirmPassword.value) {
    alert("Passwords do not match");
    return;
  }

  if (!selectedCountryId.value) {
    alert("Please select a country");
    return;
  }

  try {
    const payload = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      password: password.value,
      confirmPassword: confirmPassword.value,
      userType: selectedRole.value.toLowerCase(),
      countryId: selectedCountryId.value,
    };

    const res = await registerApi(payload);

    alert(res.message || "Registration successful!");

    router.push("/login");
  } catch (error) {
    console.error("Registration error:", error);
    alert(error.response?.data || "Registration failed.");
  }
};
</script>
